// covered-by: frontends/carrie/src/ask-v2/services/__tests__/chat.test.ts
import {
  type AskPunttCommentContext,
  type AskPunttQuotedContext,
} from "@mg/schemas/src/christo/askPuntt";
import { z } from "zod";

import { API_URL } from "../../config/env";
import { smartFetch } from "../../utils/http";

const ackMessageSchema = z.object({
  _id: z.string(),
  content: z.string(),
});

const createChatAckSchema = z.object({
  chat: z.object({ _id: z.string() }),
  userMessage: ackMessageSchema,
  assistantMessage: ackMessageSchema.optional(),
});

const sendAckSchema = z.object({
  userMessage: ackMessageSchema,
  assistantMessage: ackMessageSchema.optional(),
});

const abortAckSchema = z.object({
  message: ackMessageSchema,
});

const regenerateAckSchema = z.object({
  userMessage: ackMessageSchema,
  assistantMessage: ackMessageSchema.optional(),
});

export type AskV2ChatAck = {
  chatId?: string;
  userMessage: { id: string; content: string };
  assistantMessage?: { id: string; content: string };
};

function toAck(
  userMessage: { _id: string; content: string },
  assistantMessage?: { _id: string; content: string },
  chatId?: string,
): AskV2ChatAck {
  return {
    chatId,
    userMessage: { id: userMessage._id, content: userMessage.content },
    assistantMessage: assistantMessage
      ? { id: assistantMessage._id, content: assistantMessage.content }
      : undefined,
  };
}

const chatsUrl = `${API_URL}/private/catalyst/ask-puntt/chats`;

export type AskV2Attachment = {
  key: string;
  source: string;
  type: "image" | "video" | "file";
  name?: string;
};

// Re-export the shared wire shape under the FE name so callers
// (postToAskV2ProjectSharedFeed) keep importing it from here, without a second
// hand-mirrored copy that can drift from the schema.
export type AskV2QuotedContext = AskPunttQuotedContext;
export type AskV2CommentContext = AskPunttCommentContext;

function chatBody(
  message: string,
  attachments?: AskV2Attachment[],
  quotedContexts?: AskV2QuotedContext[],
  commentContexts?: AskV2CommentContext[],
  mentions?: string[],
  mentionEmails?: string[],
  webSearch?: boolean,
) {
  return {
    message,
    ...(attachments && attachments.length > 0 ? { attachments } : {}),
    ...(quotedContexts && quotedContexts.length > 0 ? { quotedContexts } : {}),
    ...(commentContexts && commentContexts.length > 0
      ? { commentContexts }
      : {}),
    ...(mentions && mentions.length > 0 ? { mentions } : {}),
    ...(mentionEmails && mentionEmails.length > 0 ? { mentionEmails } : {}),
    ...(webSearch ? { webSearch: true } : {}),
  };
}

export async function createAskV2Chat(
  message: string,
  attachments?: AskV2Attachment[],
  quotedContexts?: AskV2QuotedContext[],
  commentContexts?: AskV2CommentContext[],
  mentions?: string[],
  mentionEmails?: string[],
  webSearch?: boolean,
): Promise<AskV2ChatAck> {
  const json = await smartFetch(chatsUrl, {
    method: "POST",
    // why: V2 is always warhol; the existing chat resource branches on agent.
    body: JSON.stringify({
      ...chatBody(
        message,
        attachments,
        quotedContexts,
        commentContexts,
        mentions,
        mentionEmails,
        webSearch,
      ),
      agent: "warhol",
    }),
  });
  const parsed = createChatAckSchema.parse(json);
  return toAck(parsed.userMessage, parsed.assistantMessage, parsed.chat._id);
}

export async function sendAskV2Message(
  chatId: string,
  message: string,
  attachments?: AskV2Attachment[],
  quotedContexts?: AskV2QuotedContext[],
  commentContexts?: AskV2CommentContext[],
  mentions?: string[],
  mentionEmails?: string[],
  webSearch?: boolean,
): Promise<AskV2ChatAck> {
  const json = await smartFetch(`${chatsUrl}/${chatId}/messages`, {
    method: "POST",
    body: JSON.stringify(
      chatBody(
        message,
        attachments,
        quotedContexts,
        commentContexts,
        mentions,
        mentionEmails,
        webSearch,
      ),
    ),
  });
  const parsed = sendAckSchema.parse(json);
  return toAck(parsed.userMessage, parsed.assistantMessage, chatId);
}

export async function abortAskV2Message(
  chatId: string,
  messageId: string,
): Promise<void> {
  const json = await smartFetch(
    `${chatsUrl}/${chatId}/messages/${messageId}/abort`,
    { method: "POST" },
  );
  abortAckSchema.parse(json);
}

export async function regenerateAskV2Message(
  chatId: string,
  messageId: string,
  content: string,
): Promise<AskV2ChatAck> {
  const json = await smartFetch(
    `${chatsUrl}/${chatId}/messages/${messageId}/regenerate`,
    {
      method: "POST",
      body: JSON.stringify({ content }),
    },
  );
  const parsed = regenerateAckSchema.parse(json);
  return toAck(parsed.userMessage, parsed.assistantMessage, chatId);
}
